import { useState } from "react";

import { PhoneNumber } from "@/Common/Api/Contacts/Dto/PhoneNumber";
import { ClickToDialViewMode } from "@/Common/Components/CallCenter/ClickToDial.types";
import { ClickToDialViewPhoneNumber } from "@/Common/Components/CallCenter/ClickToDialViewPhoneNumber";
import { InvitationPopup } from "@/Common/Components/Portal/InvitationPopup/InvitationPopup";
import { PortalStatusButton } from "@/Common/Components/Portal/PortalStatusIcon";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { NameHelper } from "@/Common/Helpers/NameHelper";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { ContactFacetType } from "@/Common/Models/Individuals/Enums/ContactFacetType";
import { Person } from "@/Common/Models/Person/Person";

import styles from "./RelationView.module.scss";

interface IRelationInfoProps {
    person: Person;
}

function RelationInfo(props: IRelationInfoProps): JSX.Element {
    const { getText } = useTranslation();

    const [invitationPopupOpen, setInvitationPopupOpen] = useState(false);

    const person = props.person;
    const genderLookup = cacheStore.GetLookupById(person.GenderLookupId);
    const fileNumber = person.Identifier01 ?? person.FileNumber;
    const address = person.GetFormattedAddress();
    const dateOfBirth = person.GetFormattedDateOfBirth();
    const email = person.GetFormattedEmail();
    const enableInvitationFlow = cacheStore.ClinicSettings?.EnablePortalInvitationFlowV2;
    const shouldShowPortalButton = enableInvitationFlow && (cacheStore.ClinicSettings_Portal.EnablePortalV2
            || person.ContactFacetType === ContactFacetType.Patient);
    const portalButtonProps = PortalHelper.getPortalButtonPropsForRegistrationState(person.PortalSyncState, true);

    const phonesSection: JSX.Element[] = person.Phones.map((phoneNumber: PhoneNumber, idx: number) => {
        if (cacheStore.ClinicSettings.IsCallCenterEnabled()) {
            return (
                <div className={styles.infoBodyRow} key={`relation_phone_${idx}`}>
                    <span className="myle-icon-16 phone" />
                    <span className="content-text">
                        <ClickToDialViewPhoneNumber
                            viewMode={ClickToDialViewMode.Hyperlink}
                            phoneNumber={phoneNumber}
                            showPhoneType
                            forActivePatient
                        />
                    </span>
                </div>
            );
        }

        return (
            <div className={styles.infoBodyRow} key={`relation_phone_${idx}`}>
                <span className="myle-icon-16 phone" />
                <span className="content-text">{person.GetFormattedPhone(idx)}</span>
            </div>
        );
    });

    return (
        <>
            <div className={styles.infoBody}>
                <div className={styles.infoBodyRow}>
                    <h4 className={styles.name} data-cy="relation-fullName">{NameHelper.formatFullNameWithPrefixForHasName(person)}
                        {genderLookup && (
                            <span className={`myle-icon-16 ${genderLookup.LookupCode.toLowerCase()} ${styles.genderIcon}`} />
                        )}
                    </h4>
                </div>
                <div className={styles.infoBodyRow}>
                    <div className={styles.infoBodyLeft}>

                        <div className={styles.infoBodyLeft}>
                            {!IsNullOrWhiteSpace(person.GetFormattedNAM()) && (
                                <div className={styles.infoBodyRow}>
                                    <label className={styles.label}>{sessionStore.getDefaultHealthCardLabel()}</label>
                                    <span>{person.GetFormattedNAM()}</span>
                                </div>
                            )}
                            {!IsNullOrWhiteSpace(fileNumber) && (
                                <div className={styles.infoBodyRow}>
                                    <label className={styles.label}>{getText("FileNumbersLabel")}</label>
                                    <span>{fileNumber}</span>
                                </div>
                            )}
                            {!IsNullOrWhiteSpace(dateOfBirth) && (
                                <div className={styles.infoBodyRow}>
                                    <label className={styles.label}>{getText("DateofBirthLabel")}</label>
                                    <span>{`${dateOfBirth} ${person.GetFormattedAge()}`}</span>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className={styles.infoBodyRight}>
                        {phonesSection}
                        {!IsNullOrWhiteSpace(address) && (
                            <div className={styles.infoBodyRow}>
                                <span className="myle-icon-16 pin" />
                                <span className="content-text">{address}</span>
                            </div>
                        )}
                        {!IsNullOrWhiteSpace(email) && (
                            <div className={styles.infoBodyRow}>
                                <span className="myle-icon-16 pin" />
                                <span className="content-text">{email}</span>
                            </div>
                        )}
                    </div>
                    {shouldShowPortalButton && (
                        <PortalStatusButton
                            onClick={e => {
                                e.stopPropagation();
                                setInvitationPopupOpen(true);
                            }}
                            className="push-right-5"
                            {...portalButtonProps}
                        />
                    )}
                </div>
            </div>
            {enableInvitationFlow && invitationPopupOpen && (
                <InvitationPopup person={person} onCancel={() => setInvitationPopupOpen(false)} />
            )}
        </>
    );
}

interface IRelationInfoProps { person: Person; }

export { RelationInfo };
